import { useState, useEffect, useCallback, useMemo } from 'react'
import { cn } from '@/lib/utils'
import { 
  CheckCircle2, 
  ChevronDown, 
  ChevronRight, 
  File, 
  FileImage, 
  FileText, 
  Paperclip, 
  Trash2, 
  Loader2
} from 'lucide-react'
import { resourcesAPI } from '../../services/api'
import { Button } from '../ui/button'
import { Checkbox } from '../ui/checkbox'
import { Badge } from '../ui/badge'
import { ConfirmationDialog } from '../ui/confirmation-dialog'
import { useToast } from '../../hooks/use-toast'
import { useMessageInputStore } from '../../stores/messageInputStore'
import { useNavigate } from '@tanstack/react-router'

interface ResourceFile {
  name: string
  path: string
  size: number
  uploadedAt: string
  category: 'images' | 'documents' | 'others'
}

interface AttachedFile {
  id: string
  file: File
  data: ArrayBuffer
  mimeType: string
  preview?: string
  type: 'image' | 'pdf'
}

interface ResourcesTabProps {
  conversationId: string
  onAttachFiles?: (files: AttachedFile[]) => void
}

export function ResourcesTab({ conversationId }: ResourcesTabProps) {
  const [resources, setResources] = useState<{
    images: ResourceFile[]
    documents: ResourceFile[]
    others: ResourceFile[]
  }>({
    images: [],
    documents: [],
    others: []
  })
  const [selectedFiles, setSelectedFiles] = useState<Set<string>>(new Set())
  const [expandedSections, setExpandedSections] = useState<Set<string>>(new Set(['images', 'documents', 'others']))
  const [loading, setLoading] = useState(true)
  const [deleting, setDeleting] = useState(false)
  const [showDeleteDialog, setShowDeleteDialog] = useState(false)
  const { toast } = useToast()
  const { addMentions } = useMessageInputStore()
  const navigate = useNavigate()

  // Load resources
  const loadResources = useCallback(async () => {
    try {
      setLoading(true)
      const response = await resourcesAPI.get(conversationId)
      if (response.success) {
        setResources({
          images: response.resources.images as ResourceFile[],
          documents: response.resources.documents as ResourceFile[],
          others: response.resources.others as ResourceFile[]
        })
      }
    } catch (error) {
      console.error('Failed to load resources:', error)
      toast({
        title: "Error",
        description: "Failed to load resources",
        variant: "destructive",
      })
    } finally {
      setLoading(false)
    }
  }, [conversationId])

  useEffect(() => {
    loadResources()
    
    // Listen for resources-updated events
    const handleResourcesUpdated = (event: CustomEvent) => {
      if (event.detail.conversationId === conversationId) {
        console.log('📁 Resources updated, refreshing tab...')
        loadResources()
      }
    }
    
    window.addEventListener('resources-updated', handleResourcesUpdated as EventListener)
    
    return () => {
      window.removeEventListener('resources-updated', handleResourcesUpdated as EventListener)
    }
  }, [loadResources, conversationId])

  // Get file icon based on category and extension
  const getFileIcon = (file: ResourceFile) => {
    if (file.category === 'images') {
      return <FileImage className="h-4 w-4 text-primary" />
    }
    if (file.category === 'documents') {
      return <FileText className="h-4 w-4 text-green-500" />
    }
    return <File className="h-4 w-4 text-gray-500" />
  }

  // Format file size
  const formatFileSize = (bytes: number) => {
    if (bytes < 1024) return `${bytes} B`
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
  }

  // Format upload date
  const formatDate = (dateString: string) => {
    const date = new Date(dateString)
    return date.toLocaleDateString() + ' ' + date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
  }

  // Toggle section expansion
  const toggleSection = (section: string) => {
    const newExpanded = new Set(expandedSections)
    if (newExpanded.has(section)) {
      newExpanded.delete(section)
    } else {
      newExpanded.add(section)
    }
    setExpandedSections(newExpanded)
  }

  // Handle file selection
  const toggleFileSelection = (filePath: string) => {
    const newSelected = new Set(selectedFiles)
    if (newSelected.has(filePath)) {
      newSelected.delete(filePath)
    } else {
      newSelected.add(filePath)
    }
    setSelectedFiles(newSelected)
  }

  // Select all files in a category
  const toggleCategorySelection = (category: 'images' | 'documents' | 'others') => {
    const categoryFiles = resources[category]
    const categoryPaths = categoryFiles.map(f => f.path)
    const allSelected = categoryPaths.every(path => selectedFiles.has(path))
    
    const newSelected = new Set(selectedFiles)
    if (allSelected) {
      // Deselect all in category
      categoryPaths.forEach(path => newSelected.delete(path))
    } else {
      // Select all in category
      categoryPaths.forEach(path => newSelected.add(path))
    }
    setSelectedFiles(newSelected)
  }

  // Get selected files for a category
  const getSelectedInCategory = (category: 'images' | 'documents' | 'others') => {
    return resources[category].filter(file => selectedFiles.has(file.path))
  }

  // Handle file attachment via @mentions
  const handleAttachSelected = async () => {
    const allSelectedFiles = [
      ...getSelectedInCategory('images'),
      ...getSelectedInCategory('documents'),
      ...getSelectedInCategory('others')
    ]
    
    if (allSelectedFiles.length > 0) {
      // Create @mentions for each selected file
      const mentions = allSelectedFiles.map(file => `@resources/${file.path}`)
      
      // Add mentions to the store
      addMentions(mentions)
      
      // Navigate to chat view
      navigate({
        to: '/chat/$conversationId',
        params: { conversationId }
      })
      
      setSelectedFiles(new Set()) // Clear selection
      toast({
        title: "Success",
        description: `Added ${allSelectedFiles.length} file${allSelectedFiles.length > 1 ? 's' : ''} to message`,
      })
    }
  }

  // Handle file deletion
  const handleDeleteSelected = async () => {
    try {
      setDeleting(true)
      const filePaths = Array.from(selectedFiles)
      const response = await resourcesAPI.delete(conversationId, filePaths)
      
      if (response.success) {
        toast({
          title: "Success",
          description: response.message,
        })
        setSelectedFiles(new Set())
        await loadResources() // Reload to show updated list
      } else {
        toast({
          title: "Error",
          description: "Failed to delete some files",
          variant: "destructive",
        })
      }
    } catch (error) {
      console.error('Failed to delete resources:', error)
      toast({
        title: "Error",
        description: "Failed to delete files",
        variant: "destructive",
      })
    } finally {
      setDeleting(false)
      setShowDeleteDialog(false)
    }
  }

  // Get total files count
  const totalFiles = resources.images.length + resources.documents.length + resources.others.length
  const selectedCount = selectedFiles.size

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    )
  }

  return (
    <div className="h-full flex flex-col bg-background">
      {/* Header */}
      <div className="flex-shrink-0 p-4 border-b border-border/30">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <h3 className="text-sm font-semibold">Resources</h3>
            <Badge variant="secondary" className="text-xs">
              {totalFiles} files
            </Badge>
            {selectedCount > 0 && (
              <Badge variant="default" className="text-xs">
                {selectedCount} selected
              </Badge>
            )}
          </div>
          
          {selectedCount > 0 && (
            <div className="flex gap-2">
              <Button
                size="sm"
                variant="outline"
                onClick={handleAttachSelected}
                className="h-7 text-xs flex items-center gap-1.5"
              >
                <Paperclip className="h-3 w-3" />
                Attach ({selectedCount})
              </Button>
              <Button
                size="sm"
                variant="destructive"
                onClick={() => setShowDeleteDialog(true)}
                className="h-7 text-xs flex items-center gap-1.5"
              >
                <Trash2 className="h-3 w-3" />
                Delete ({selectedCount})
              </Button>
            </div>
          )}
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-auto">
        {totalFiles === 0 ? (
          <div className="flex flex-col items-center justify-center h-64 text-center">
            <File className="h-12 w-12 text-muted-foreground/50 mb-2" />
            <p className="text-sm text-muted-foreground">No resources uploaded yet</p>
            <p className="text-xs text-muted-foreground">Upload files to see them here</p>
          </div>
        ) : (
          <div className="space-y-4 p-4">
            {/* Images Section */}
            {resources.images.length > 0 && (
              <ResourceSection
                title="Images"
                icon="🖼️"
                files={resources.images}
                selectedFiles={selectedFiles}
                expanded={expandedSections.has('images')}
                onToggleExpanded={() => toggleSection('images')}
                onToggleFile={toggleFileSelection}
                onToggleAll={() => toggleCategorySelection('images')}
                getFileIcon={getFileIcon}
                formatFileSize={formatFileSize}
                formatDate={formatDate}
                conversationId={conversationId}
              />
            )}

            {/* Documents Section */}
            {resources.documents.length > 0 && (
              <ResourceSection
                title="Documents"
                icon="📄"
                files={resources.documents}
                selectedFiles={selectedFiles}
                expanded={expandedSections.has('documents')}
                onToggleExpanded={() => toggleSection('documents')}
                onToggleFile={toggleFileSelection}
                onToggleAll={() => toggleCategorySelection('documents')}
                getFileIcon={getFileIcon}
                formatFileSize={formatFileSize}
                formatDate={formatDate}
                conversationId={conversationId}
              />
            )}

            {/* Others Section */}
            {resources.others.length > 0 && (
              <ResourceSection
                title="Other Files"
                icon="📎"
                files={resources.others}
                selectedFiles={selectedFiles}
                expanded={expandedSections.has('others')}
                onToggleExpanded={() => toggleSection('others')}
                onToggleFile={toggleFileSelection}
                onToggleAll={() => toggleCategorySelection('others')}
                getFileIcon={getFileIcon}
                formatFileSize={formatFileSize}
                formatDate={formatDate}
                conversationId={conversationId}
              />
            )}
          </div>
        )}
      </div>

      {/* Delete Confirmation Dialog */}
      <ConfirmationDialog
        open={showDeleteDialog}
        onOpenChange={setShowDeleteDialog}
        title="Delete Files"
        description={`Are you sure you want to delete ${selectedCount} file${selectedCount > 1 ? 's' : ''}? This action cannot be undone and will also create a git commit.`}
        confirmText={deleting ? "Deleting..." : "Delete Files"}
        variant="destructive"
        onConfirm={handleDeleteSelected}
        onCancel={() => setShowDeleteDialog(false)}
      />
    </div>
  )
}

// Image Preview Component with loading and error states
interface ImagePreviewProps {
  src: string
  alt: string
  fallbackIcon: React.ReactNode
}

function ImagePreview({ src, alt, fallbackIcon }: ImagePreviewProps) {
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(false)
  const [currentSrc, setCurrentSrc] = useState(src)
  const [attemptCount, setAttemptCount] = useState(0)

  useEffect(() => {
    console.log('ImagePreview loading:', src)
    console.log('API URL from env:', import.meta.env.VITE_API_URL)
    // Force a new image load by resetting state
    setCurrentSrc('')
    setLoading(true)
    setError(false)
    setAttemptCount(0)
    // Set the new src after a micro delay to force reload
    setTimeout(() => {
      setCurrentSrc(src)
    }, 10)
  }, [src])

  const handleLoad = () => {
    setLoading(false)
    setError(false)
    console.log('Image loaded successfully:', currentSrc)
  }

  const handleError = () => {
    console.error('Failed to load image:', currentSrc)
    
    // Try alternative URL formats
    if (attemptCount === 0) {
      // Try without /api prefix (direct path)
      const directUrl = currentSrc.replace('/api/', '/')
      console.log('Trying direct URL:', directUrl)
      setCurrentSrc(directUrl)
      setAttemptCount(1)
      return
    } else if (attemptCount === 1) {
      // Try absolute path to API
      const absoluteUrl = `${window.location.origin}${src}`
      console.log('Trying absolute URL:', absoluteUrl)
      setCurrentSrc(absoluteUrl)
      setAttemptCount(2)
      return
    }
    
    // All attempts failed
    setLoading(false)
    setError(true)
    console.error('All image loading attempts failed for:', src)
  }

  return (
    <div className="w-full h-full relative">
      {!error && (
        <img
          key={src} // Force React to recreate the element when src changes
          src={currentSrc}
          alt={alt}
          className="w-full h-full object-cover"
          onLoad={handleLoad}
          onError={handleError}
          style={{ display: loading ? 'none' : 'block' }}
        />
      )}
      
      {/* Loading state */}
      {loading && !error && (
        <div className="w-full h-full flex items-center justify-center">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </div>
      )}
      
      {/* Error/fallback state */}
      {error && (
        <div className="w-full h-full flex items-center justify-center">
          <div className="scale-150">
            {fallbackIcon}
          </div>
        </div>
      )}
    </div>
  )
}

// Resource Section Component
interface ResourceSectionProps {
  title: string
  icon: string
  files: ResourceFile[]
  selectedFiles: Set<string>
  expanded: boolean
  onToggleExpanded: () => void
  onToggleFile: (path: string) => void
  onToggleAll: () => void
  getFileIcon: (file: ResourceFile) => React.ReactNode
  formatFileSize: (bytes: number) => string
  formatDate: (date: string) => string
  conversationId: string
}

function ResourceSection({
  title,
  icon,
  files,
  selectedFiles,
  expanded,
  onToggleExpanded,
  onToggleFile,
  onToggleAll,
  getFileIcon,
  formatFileSize,
  formatDate,
  conversationId
}: ResourceSectionProps) {
  const selectedInSection = files.filter(file => selectedFiles.has(file.path)).length
  const allSelected = selectedInSection === files.length
  const someSelected = selectedInSection > 0 && selectedInSection < files.length

  return (
    <div className="border border-border/30 rounded-lg overflow-hidden">
      {/* Section Header */}
      <div className="bg-muted/30 p-3 border-b border-border/30">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={onToggleExpanded}
              className="h-6 w-6 p-0 hover:bg-background/50"
            >
              {expanded ? (
                <ChevronDown className="h-3 w-3" />
              ) : (
                <ChevronRight className="h-3 w-3" />
              )}
            </Button>
            <span className="text-sm">{icon}</span>
            <span className="text-sm font-medium">{title}</span>
            <Badge variant="outline" className="text-xs">
              {files.length}
            </Badge>
            {selectedInSection > 0 && (
              <Badge variant="default" className="text-xs">
                {selectedInSection} selected
              </Badge>
            )}
          </div>
          
          <Checkbox
            checked={allSelected}
            onCheckedChange={onToggleAll}
            className="h-4 w-4"
            data-indeterminate={someSelected}
          />
        </div>
      </div>

      {/* Section Content */}
      {expanded && (
        <div className="p-3">
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3">
            {files.map((file) => (
              <div
                key={file.path}
                className={cn(
                  "relative group border border-border/30 rounded-lg overflow-hidden hover:border-border hover:shadow-sm transition-all cursor-move",
                  selectedFiles.has(file.path) && "border-primary bg-primary/5"
                )}
                draggable
                onDragStart={(e) => {
                  // Set drag data with the @mention path
                  e.dataTransfer.effectAllowed = 'copy';
                  e.dataTransfer.setData('text/plain', `@resources/${file.path}`);
                  e.dataTransfer.setData('resource/mention', `@resources/${file.path}`);
                  
                  // Add visual feedback
                  e.currentTarget.style.opacity = '0.5';
                }}
                onDragEnd={(e) => {
                  // Reset visual feedback
                  e.currentTarget.style.opacity = '';
                }}
                onClick={() => onToggleFile(file.path)}
              >
                {/* File Preview */}
                <div className="aspect-square bg-muted/20 flex items-center justify-center relative">
                  {file.category === 'images' ? (
                    <ImagePreview
                      src={useMemo(() => {
                        // Use stable cache key based on file path and conversation
                        // This will only change when the file actually changes
                        const baseUrl = `/api/filesystem/image/resources%2F${encodeURIComponent(file.path)}?conversationId=${conversationId}`;
                        const version = `${conversationId}_${file.path.replace(/[^a-zA-Z0-9]/g, '_')}_${file.size || 0}`;
                        return `${baseUrl}&v=${version}`;
                      }, [file.path, file.size, conversationId])}
                      alt={file.name}
                      fallbackIcon={getFileIcon(file)}
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <div className="scale-150">
                        {getFileIcon(file)}
                      </div>
                    </div>
                  )}

                  {/* Selection checkbox */}
                  <div className="absolute top-1 right-1">
                    <Checkbox
                      checked={selectedFiles.has(file.path)}
                      className="h-4 w-4 bg-background border-border shadow-sm"
                    />
                  </div>

                  {/* Selection overlay */}
                  {selectedFiles.has(file.path) && (
                    <div className="absolute inset-0 bg-primary/10 flex items-center justify-center">
                      <CheckCircle2 className="h-6 w-6 text-primary" />
                    </div>
                  )}
                </div>

                {/* File Info */}
                <div className="p-2 bg-background/95 backdrop-blur-sm">
                  <div className="text-xs font-medium truncate" title={file.name}>
                    {file.name}
                  </div>
                  <div className="text-xs text-muted-foreground">
                    {formatFileSize(file.size)}
                  </div>
                  <div className="text-xs text-muted-foreground">
                    {formatDate(file.uploadedAt)}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}